// This file is auto-generated by @hey-api/openapi-ts

import type { Client, ClientMeta, Options as Options2, RequestResult, TDataShape } from "./client";
import { client } from "./client.gen";
import type {
  AdminActivityActionsData,
  AdminActivityActionsResponses,
  AdminActivityAnalyticsData,
  AdminActivityAnalyticsErrors,
  AdminActivityAnalyticsResponses,
  AdminDeleteFeedbackData,
  AdminDeleteFeedbackErrors,
  AdminDeleteFeedbackResponses,
  AdminDeleteUserData,
  AdminDeleteUserErrors,
  AdminDeleteUserResponses,
  AdminGetFeedbackScreenshotData,
  AdminGetFeedbackScreenshotErrors,
  AdminGetFeedbackScreenshotResponses,
  AdminGetUserData,
  AdminGetUserErrors,
  AdminGetUserResponses,
  AdminInviteUserData,
  AdminInviteUserErrors,
  AdminInviteUserResponses,
  AdminListActivityData,
  AdminListActivityErrors,
  AdminListActivityResponses,
  AdminListFeedbackData,
  AdminListFeedbackErrors,
  AdminListFeedbackResponses,
  AdminListUsersData,
  AdminListUsersResponses,
  AdminUpdateUserData,
  AdminUpdateUserErrors,
  AdminUpdateUserResponses,
  ChangePasswordData,
  ChangePasswordErrors,
  ChangePasswordResponses,
  GetActivityData,
  GetActivityResponses,
  GetHealthData,
  GetHealthResponses,
  GetMeData,
  GetMeDetailData,
  GetMeDetailResponses,
  GetMeResponses,
  GetMyVoteData,
  GetMyVoteResponses,
  GetVoteSummaryData,
  GetVoteSummaryResponses,
  GetVoteThemesData,
  GetVoteThemesResponses,
  LoginData,
  LoginErrors,
  LoginResponses,
  LogoutData,
  LogoutResponses,
  ResetVotesData,
  ResetVotesResponses,
  SignupData,
  SignupErrors,
  SignupResponses,
  SubmitFeedbackData,
  SubmitFeedbackErrors,
  SubmitFeedbackResponses,
  SubmitVoteData,
  SubmitVoteErrors,
  SubmitVoteResponses,
  UpdateProfileData,
  UpdateProfileErrors,
  UpdateProfileResponses,
} from "./types.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: keyof ClientMeta extends never ? Record<string, unknown> : ClientMeta;
};

/**
 * Submit Feedback
 *
 * Persist a feedback row submitted by the current user.
 */
export const submitFeedback = <ThrowOnError extends boolean = false>(
  options: Options<SubmitFeedbackData, ThrowOnError>,
): RequestResult<SubmitFeedbackResponses, SubmitFeedbackErrors, ThrowOnError> =>
  (options.client ?? client).post<SubmitFeedbackResponses, SubmitFeedbackErrors, ThrowOnError>({
    url: "/feedback",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin List Feedback
 *
 * List feedback entries (admin only).
 *
 * Search, filtering, and pagination are applied server-side by the
 * list_feedback RPC. `rating=0` selects entries submitted without a rating;
 * 1–5 match exactly. The response also carries the full-table distribution
 * for the chart, which deliberately ignores the active filters.
 */
export const adminListFeedback = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListFeedbackData, ThrowOnError>,
): RequestResult<AdminListFeedbackResponses, AdminListFeedbackErrors, ThrowOnError> =>
  (options?.client ?? client).get<
    AdminListFeedbackResponses,
    AdminListFeedbackErrors,
    ThrowOnError
  >({ url: "/admin/feedback", ...options });

/**
 * Admin Get Feedback Screenshot
 *
 * Return the inline screenshot data URL for one feedback row (admin only).
 *
 * Split out from the list endpoint so the admin page only pays the per-row
 * PNG cost for the rows actually on screen.
 */
export const adminGetFeedbackScreenshot = <ThrowOnError extends boolean = false>(
  options: Options<AdminGetFeedbackScreenshotData, ThrowOnError>,
): RequestResult<
  AdminGetFeedbackScreenshotResponses,
  AdminGetFeedbackScreenshotErrors,
  ThrowOnError
> =>
  (options.client ?? client).get<
    AdminGetFeedbackScreenshotResponses,
    AdminGetFeedbackScreenshotErrors,
    ThrowOnError
  >({ url: "/admin/feedback/{feedback_id}/screenshot", ...options });

/**
 * Admin Delete Feedback
 *
 * Resolve (hard-delete) a feedback entry (admin only).
 *
 * There is no triage workflow on this surface, so admins close feedback out
 * by removing the row. Deleting an id that's already gone returns 404 so the
 * UI can prompt a refresh instead of silently no-op'ing.
 */
export const adminDeleteFeedback = <ThrowOnError extends boolean = false>(
  options: Options<AdminDeleteFeedbackData, ThrowOnError>,
): RequestResult<AdminDeleteFeedbackResponses, AdminDeleteFeedbackErrors, ThrowOnError> =>
  (options.client ?? client).delete<
    AdminDeleteFeedbackResponses,
    AdminDeleteFeedbackErrors,
    ThrowOnError
  >({ url: "/admin/feedback/{feedback_id}", ...options });

/**
 * Reset Votes
 *
 * Delete every vote and clear the clustering state — a between-demo reset.
 *
 * Gated by ``SessionDep``: it requires an authenticated (presenter) session,
 * so the public ``/vote`` audience cannot wipe the ballot.
 */
export const resetVotes = <ThrowOnError extends boolean = false>(
  options?: Options<ResetVotesData, ThrowOnError>,
): RequestResult<ResetVotesResponses, unknown, ThrowOnError> =>
  (options?.client ?? client).delete<ResetVotesResponses, unknown, ThrowOnError>({
    url: "/votes",
    ...options,
  });

/**
 * Submit Vote
 *
 * Record (or update) the caller's vote, keyed on the ``boi-vote-id`` cookie.
 *
 * A first-time voter has no valid cookie, so one is generated and set on the
 * response; a malformed cookie is treated as first-time. Re-voting from the
 * same browser reuses the cookie and upserts the existing row rather than
 * adding a duplicate. Once the voter cap is reached, only existing voters may
 * keep updating — a new voter is rejected with 429.
 */
export const submitVote = <ThrowOnError extends boolean = false>(
  options: Options<SubmitVoteData, ThrowOnError>,
): RequestResult<SubmitVoteResponses, SubmitVoteErrors, ThrowOnError> =>
  (options.client ?? client).post<SubmitVoteResponses, SubmitVoteErrors, ThrowOnError>({
    url: "/votes",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Get My Vote
 *
 * Return the caller's own vote, resolved from their ``boi-vote-id`` cookie.
 *
 * No cookie, a malformed cookie, or no stored row → ``voted=false`` with a null
 * vote; otherwise the stored score and comments come back so the /vote page can
 * prefill an edit.
 */
export const getMyVote = <ThrowOnError extends boolean = false>(
  options?: Options<GetMyVoteData, ThrowOnError>,
): RequestResult<GetMyVoteResponses, unknown, ThrowOnError> =>
  (options?.client ?? client).get<GetMyVoteResponses, unknown, ThrowOnError>({
    url: "/votes/me",
    ...options,
  });

/**
 * Get Vote Summary
 *
 * Live vote stats for the presenter: total, average, and a 1-5 distribution.
 *
 * Cheap enough for the frontend to poll every few seconds — one store read and
 * a Python aggregation, no LLM. ``distribution`` is always five zero-filled
 * buckets (scores 1..5); ``average`` is null when no votes exist.
 */
export const getVoteSummary = <ThrowOnError extends boolean = false>(
  options?: Options<GetVoteSummaryData, ThrowOnError>,
): RequestResult<GetVoteSummaryResponses, unknown, ThrowOnError> =>
  (options?.client ?? client).get<GetVoteSummaryResponses, unknown, ThrowOnError>({
    url: "/votes/summary",
    ...options,
  });

/**
 * Get Vote Themes
 *
 * Cluster audience pros and cons into labeled themes for the presenter.
 *
 * The presenter auto-polls this while votes stream in. Pros and cons are
 * clustered concurrently and independently, each biggest-theme-first. Existing
 * themes are frozen across polls and only appended to, so labels and groupings
 * stay stable. Counts and verbatim examples are always derived deterministically
 * — the model never authors a count or a quote — and a clustering failure
 * degrades rather than 500s.
 */
export const getVoteThemes = <ThrowOnError extends boolean = false>(
  options?: Options<GetVoteThemesData, ThrowOnError>,
): RequestResult<GetVoteThemesResponses, unknown, ThrowOnError> =>
  (options?.client ?? client).get<GetVoteThemesResponses, unknown, ThrowOnError>({
    url: "/votes/themes",
    ...options,
  });

/**
 * Health
 *
 * Return a simple health status payload.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
): RequestResult<GetHealthResponses, unknown, ThrowOnError> =>
  (options?.client ?? client).get<GetHealthResponses, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });

/**
 * Auth Signup
 *
 * Register a new user with email verification.
 */
export const signup = <ThrowOnError extends boolean = false>(
  options: Options<SignupData, ThrowOnError>,
): RequestResult<SignupResponses, SignupErrors, ThrowOnError> =>
  (options.client ?? client).post<SignupResponses, SignupErrors, ThrowOnError>({
    url: "/auth/signup",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Login
 *
 * Authenticate via Supabase, then set the signed session cookie.
 *
 * The Supabase access/refresh tokens never leave the backend — the browser
 * only ever sees the HMAC cookie this endpoint sets.
 */
export const login = <ThrowOnError extends boolean = false>(
  options: Options<LoginData, ThrowOnError>,
): RequestResult<LoginResponses, LoginErrors, ThrowOnError> =>
  (options.client ?? client).post<LoginResponses, LoginErrors, ThrowOnError>({
    url: "/auth/login",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Logout
 *
 * Sign out — clear the session cookie.
 */
export const logout = <ThrowOnError extends boolean = false>(
  options?: Options<LogoutData, ThrowOnError>,
): RequestResult<LogoutResponses, unknown, ThrowOnError> =>
  (options?.client ?? client).post<LogoutResponses, unknown, ThrowOnError>({
    url: "/auth/logout",
    ...options,
  });

/**
 * Auth Me
 *
 * Get the current user's profile.
 */
export const getMe = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeData, ThrowOnError>,
): RequestResult<GetMeResponses, unknown, ThrowOnError> =>
  (options?.client ?? client).get<GetMeResponses, unknown, ThrowOnError>({
    url: "/auth/me",
    ...options,
  });

/**
 * Auth Me Detail
 *
 * Get the current user's profile with timeline fields.
 */
export const getMeDetail = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeDetailData, ThrowOnError>,
): RequestResult<GetMeDetailResponses, unknown, ThrowOnError> =>
  (options?.client ?? client).get<GetMeDetailResponses, unknown, ThrowOnError>({
    url: "/auth/me/detail",
    ...options,
  });

/**
 * Auth Update Profile
 *
 * Update current user's name.
 */
export const updateProfile = <ThrowOnError extends boolean = false>(
  options: Options<UpdateProfileData, ThrowOnError>,
): RequestResult<UpdateProfileResponses, UpdateProfileErrors, ThrowOnError> =>
  (options.client ?? client).put<UpdateProfileResponses, UpdateProfileErrors, ThrowOnError>({
    url: "/auth/profile",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Change Password
 *
 * Change the current user's password (verifies current password first).
 */
export const changePassword = <ThrowOnError extends boolean = false>(
  options: Options<ChangePasswordData, ThrowOnError>,
): RequestResult<ChangePasswordResponses, ChangePasswordErrors, ThrowOnError> =>
  (options.client ?? client).put<ChangePasswordResponses, ChangePasswordErrors, ThrowOnError>({
    url: "/auth/password",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Activity
 *
 * Get the current user's activity log.
 */
export const getActivity = <ThrowOnError extends boolean = false>(
  options?: Options<GetActivityData, ThrowOnError>,
): RequestResult<GetActivityResponses, unknown, ThrowOnError> =>
  (options?.client ?? client).get<GetActivityResponses, unknown, ThrowOnError>({
    url: "/auth/activity",
    ...options,
  });

/**
 * Admin List Users
 *
 * List all users (admin only).
 */
export const adminListUsers = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListUsersData, ThrowOnError>,
): RequestResult<AdminListUsersResponses, unknown, ThrowOnError> =>
  (options?.client ?? client).get<AdminListUsersResponses, unknown, ThrowOnError>({
    url: "/admin/users",
    ...options,
  });

/**
 * Admin Delete User
 *
 * Delete a user (admin only). Self-deletion blocked.
 */
export const adminDeleteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminDeleteUserData, ThrowOnError>,
): RequestResult<AdminDeleteUserResponses, AdminDeleteUserErrors, ThrowOnError> =>
  (options.client ?? client).delete<AdminDeleteUserResponses, AdminDeleteUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Get User
 *
 * Get a single user detail with activity logs (admin only).
 */
export const adminGetUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminGetUserData, ThrowOnError>,
): RequestResult<AdminGetUserResponses, AdminGetUserErrors, ThrowOnError> =>
  (options.client ?? client).get<AdminGetUserResponses, AdminGetUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Update User
 *
 * Update a user's name or role (admin only). Self-demotion blocked.
 */
export const adminUpdateUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateUserData, ThrowOnError>,
): RequestResult<AdminUpdateUserResponses, AdminUpdateUserErrors, ThrowOnError> =>
  (options.client ?? client).put<AdminUpdateUserResponses, AdminUpdateUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin Invite User
 *
 * Invite a user by email (admin only).
 */
export const adminInviteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminInviteUserData, ThrowOnError>,
): RequestResult<AdminInviteUserResponses, AdminInviteUserErrors, ThrowOnError> =>
  (options.client ?? client).post<AdminInviteUserResponses, AdminInviteUserErrors, ThrowOnError>({
    url: "/admin/invite",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin List Activity
 *
 * List activity across all users (admin only).
 *
 * Search, filtering, sorting, and pagination are all applied server-side by
 * the list_recent_activity RPC — the client sends params and renders results.
 */
export const adminListActivity = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListActivityData, ThrowOnError>,
): RequestResult<AdminListActivityResponses, AdminListActivityErrors, ThrowOnError> =>
  (options?.client ?? client).get<
    AdminListActivityResponses,
    AdminListActivityErrors,
    ThrowOnError
  >({ url: "/admin/activity", ...options });

/**
 * Admin Activity Analytics
 *
 * Aggregated activity analytics for the admin dashboard (admin only).
 */
export const adminActivityAnalytics = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityAnalyticsData, ThrowOnError>,
): RequestResult<AdminActivityAnalyticsResponses, AdminActivityAnalyticsErrors, ThrowOnError> =>
  (options?.client ?? client).get<
    AdminActivityAnalyticsResponses,
    AdminActivityAnalyticsErrors,
    ThrowOnError
  >({ url: "/admin/activity/analytics", ...options });

/**
 * Admin Activity Actions
 *
 * Distinct activity actions across all time (admin only).
 *
 * Used to populate the activity filter dropdown so it matches the full,
 * unbounded table scope rather than the windowed analytics breakdown.
 */
export const adminActivityActions = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityActionsData, ThrowOnError>,
): RequestResult<AdminActivityActionsResponses, unknown, ThrowOnError> =>
  (options?.client ?? client).get<AdminActivityActionsResponses, unknown, ThrowOnError>({
    url: "/admin/activity/actions",
    ...options,
  });
